import { useState, useEffect } from 'react';
import AxiosInstance from 'src/components/Axios';

// Модульні кеші — живуть протягом сесії браузера.
// Скидаються вручну через invalidateResponderTeams() або при перезавантаженні сторінки.
let cacheActive = null;
let cacheInactive = null;

/**
 * Скидає обидва кеші команд (активні та неактивні).
 * Викликати після успішного створення або редагування команди у SupportTeamsForm,
 * щоб наступні виклики хуків зробили свіжі запити на сервер.
 */
export const invalidateResponderTeams = () => {
	cacheActive = null;
	cacheInactive = null;
};

/**
 * Хук для отримання списку активних команд підтримки (`/responder-teams?is_active=true`).
 *
 * Перший виклик робить HTTP-запит і зберігає результат у модульному кеші.
 * Усі наступні виклики (навіть у різних компонентах) повертають кешовані дані
 * без повторного запиту. Для примусового оновлення — викликати invalidateResponderTeams()
 * перед наступним рендером компонента, що використовує цей хук.
 *
 * @returns {Array} Масив об'єктів активних команд. До завершення запиту
 *   повертає порожній масив (або вже кешовані дані, якщо кеш заповнений).
 *
 * @example
 * const teams = useResponderTeams();
 * // [{ id: 1, code: 'ASD', name: 'Команда ASD', is_active: true }, ...]
 */
export const useResponderTeams = () => {
	const [items, setItems] = useState(cacheActive || []);

	useEffect(() => {
		if (cacheActive) return;
		AxiosInstance.get('/responder-teams', { params: { is_active: true, itemsPerPage: 100 } })
			.then(res => {
				const list = Array.isArray(res.data) ? res.data : (res.data.data ?? []);
				cacheActive = list;
				setItems(list);
			})
			.catch(() => {});
	}, []);

	return items;
};

/**
 * Хук для отримання списку неактивних команд підтримки (`/responder-teams?is_active=false`).
 *
 * Поведінка кешування аналогічна useResponderTeams — один запит на сесію.
 * Кеш спільно скидається через invalidateResponderTeams().
 *
 * Використовується для відображення застарілих (відключених) команд,
 * що вже прив'язані до запитів підтримки.
 *
 * @returns {Array} Масив об'єктів неактивних команд. До завершення запиту
 *   повертає порожній масив (або вже кешовані дані, якщо кеш заповнений).
 *
 * @example
 * const inactiveTeams = useInactiveResponderTeams();
 * // [{ id: 3, code: 'OLD', name: 'Стара команда', is_active: false }, ...]
 */
export const useInactiveResponderTeams = () => {
	const [items, setItems] = useState(cacheInactive || []);

	useEffect(() => {
		if (cacheInactive) return;
		AxiosInstance.get('/responder-teams', { params: { is_active: false, itemsPerPage: 100 } })
			.then(res => {
				const list = Array.isArray(res.data) ? res.data : (res.data.data ?? []);
				cacheInactive = list;
				setItems(list);
			})
			.catch(() => {});
	}, []);

	return items;
};
